import {
  CHASSIS_TOP,
  SCREEN_COLS,
  SCREEN_ROWS,
  KEYBOARD_NORMAL,
  KEYBOARD_Y_HIT,
  BIRD_FRAMES,
  HIGHLIGHT_START,
  HIGHLIGHT_END,
} from "./frames.ts";
import type { GameState, RenderOutput, RenderSegment } from "./types.ts";

function padLine(line: string): string {
  if (line.length >= SCREEN_COLS) return line.slice(0, SCREEN_COLS);
  return line.padEnd(SCREEN_COLS, " ");
}

function statusLine(state: GameState): string {
  const hearts = "+".repeat(state.lives).padEnd(3, " ");
  const score = String(state.score).padStart(4, "0");
  const round = String(state.round).padStart(2, "0");
  return `LIVES:${hearts}  SCORE:${score}  ROUND:${round}`;
}

function birdFramesFor(state: GameState): string[] {
  if (state.birdAnim === "peck") return BIRD_FRAMES.peck_contact;
  if (state.birdAnim === "slump") return BIRD_FRAMES.slump;
  return BIRD_FRAMES.idle;
}

// Overlay bird sprite rows into screen lines starting at rowStart.
// colStart anchors the bird's left edge so its beak (which moves leftward when
// pecking) reaches toward the prompt area in the middle of the screen.
function overlayBird(
  lines: string[],
  birdRows: string[],
  rowStart: number,
  colStart = 22,
): void {
  for (let i = 0; i < birdRows.length; i++) {
    const row = rowStart + i;
    if (row >= SCREEN_ROWS) break;
    const existing = (lines[row] ?? "").padEnd(colStart, " ");
    lines[row] = existing.slice(0, colStart) + birdRows[i]!;
  }
}

function screenLinesFor(state: GameState): string[] {
  const lines: string[] = new Array(SCREEN_ROWS).fill("");
  const birdRows = birdFramesFor(state);

  switch (state.phase) {
    case "IDLE":
      lines[1] = "        DRINKING  BIRD";
      lines[2] = "        ===============";
      lines[4] = "  TOP SCORES:";
      state.leaderboard.slice(0, 5).forEach((e, i) => {
        const n = String(e.score).padStart(5, " ");
        lines[5 + i] = `    ${i + 1}. ${e.initials}    ${n}    R${String(e.round).padStart(2, "0")}`;
      });
      lines[12] = "       PRESS Y TO START";
      // Idle screen: show bird in lower-right too so it's visible at title
      overlayBird(lines, BIRD_FRAMES.idle, 2);
      break;

    case "ASKING":
      lines[0] = `> ${state.currentQuestion}`;
      overlayBird(lines, birdRows, 3);
      lines[13] = statusLine(state);
      break;

    case "PROMPT":
      lines[0] = `> ${state.currentQuestion}`;
      lines[2] = "_ PRESS Y TO CONTINUE";
      overlayBird(lines, birdRows, 3);
      lines[13] = statusLine(state);
      break;

    case "FEEDBACK":
      lines[0] = `> ${state.currentQuestion}`;
      lines[2] = `>>> ${state.lastFeedback}! <<<`;
      lines[3] = `    (${state.lastReactionMs}MS)`;
      overlayBird(lines, birdRows, 4);
      lines[13] = statusLine(state);
      break;

    case "PENALTY":
      lines[0] = `> ${state.currentQuestion}`;
      lines[2] = `!!! ${state.lastFeedback} !!!`;
      lines[3] = `    LIFE LOST  (${state.lives} LEFT)`;
      overlayBird(lines, birdRows, 4);
      lines[13] = statusLine(state);
      break;

    case "GAME_OVER":
      lines[2] = "           GAME  OVER";
      lines[3] = "           ===========";
      lines[5] = `       FINAL SCORE: ${state.score}`;
      lines[6] = `       REACHED ROUND ${state.round}`;
      overlayBird(lines, BIRD_FRAMES.slump, 3);
      break;

    case "LEADERBOARD_ENTRY": {
      lines[1] = "         NEW HIGH SCORE!";
      lines[3] = `         SCORE: ${state.score}`;
      const chars = state.initials.split("");
      const marker = (i: number): string =>
        i === state.initialsCursor ? `[${chars[i]}]` : ` ${chars[i]} `;
      lines[6] = `             ${marker(0)}${marker(1)}${marker(2)}`;
      lines[9] = "        UP/DOWN: CHANGE LETTER";
      lines[10] = "        Y:       CONFIRM";
      break;
    }

    case "BOOT":
      break;
  }
  return lines.map(padLine);
}

function toSegments(sceneStr: string): RenderSegment[] {
  const out: RenderSegment[] = [];
  let cursor = 0;
  while (cursor < sceneStr.length) {
    const start = sceneStr.indexOf(HIGHLIGHT_START, cursor);
    if (start === -1) {
      out.push({ text: sceneStr.slice(cursor), highlight: false });
      break;
    }
    if (start > cursor) {
      out.push({ text: sceneStr.slice(cursor, start), highlight: false });
    }
    const end = sceneStr.indexOf(HIGHLIGHT_END, start + 1);
    if (end === -1) {
      out.push({ text: sceneStr.slice(start + 1), highlight: true });
      break;
    }
    out.push({ text: sceneStr.slice(start + 1, end), highlight: true });
    cursor = end + 1;
  }
  return out;
}

export function render(state: GameState): RenderOutput {
  const screenLines = screenLinesFor(state);
  let chassis = CHASSIS_TOP;
  screenLines.forEach((line, i) => {
    chassis = chassis.replace(`{{SCREEN_${i + 1}}}`, line);
  });
  const keyboard = state.birdAnim === "peck" ? KEYBOARD_Y_HIT : KEYBOARD_NORMAL;
  const scene = `${chassis}\n${keyboard}`;

  return {
    segments: toSegments(scene),
    feedbackText: state.lastFeedback ?? "",
    leaderboardText: state.leaderboard
      .map(
        (e, i) =>
          `${i + 1}. ${e.initials}  ${String(e.score).padStart(5, " ")}  R${e.round}`,
      )
      .join("\n"),
  };
}
